import type { IncomingMessage, ServerResponse } from 'node:http';

import { resolveVWOVisitorId, VWO_COOKIE_CONFIG } from '@aspendental/shared-components-web';
import { parse as parseCookie } from 'cookie';
import type { Logger } from 'pino';

function findVisitorIdInSetCookie(res: ServerResponse): string | null {
	const header = res.getHeader('Set-Cookie');
	if (!header) return null;
	const entries = Array.isArray(header) ? header : [String(header)];
	const prefix = `${VWO_COOKIE_CONFIG.name}=`;
	for (const entry of entries) {
		if (typeof entry !== 'string') continue;
		if (entry.startsWith(prefix)) {
			return entry.slice(prefix.length).split(';')[0];
		}
	}
	return null;
}

export function getOrCreateVisitorId(req: IncomingMessage, res: ServerResponse, logger: Logger): string {
	const cookies = parseCookie(req.headers.cookie || '');
	const existing = cookies[VWO_COOKIE_CONFIG.name];
	if (existing) {
		logger.info(`[VWO FME] Using existing visitor ID: ${existing}`);
		return existing;
	}

	// Another resolver in this same request may have already minted an ID and
	// queued a Set-Cookie. Reuse it so all VWO calls on a cold request bucket
	// the visitor consistently.
	const alreadyEmitted = findVisitorIdInSetCookie(res);
	if (alreadyEmitted) {
		logger.info(`[VWO FME] Reusing visitor ID already queued on response: ${alreadyEmitted}`);
		return alreadyEmitted;
	}

	// No cookie on the request and none already queued by another resolver:
	// this is either a brand-new real visitor or a bot/crawler. Bots must
	// never have a VWO identity minted for them — otherwise every cookieless
	// crawl inflates VWO's own Users count — so delegate to the shared
	// resolver, which detects bots via user-agent and returns an empty
	// visitor ID for them instead of generating one.
	const userAgent = req.headers['user-agent'];
	const { visitorId, isNewVisitor } = resolveVWOVisitorId({
		userAgent,
		// Deliberately undefined, not `existing` — we already returned above
		// if a cookie was present. By this point the only two possibilities
		// are "new real visitor" or "excluded bot".
		existingCookieValue: undefined,
	});

	if (!isNewVisitor) {
		// Detected bot: no visitor ID minted, no Set-Cookie, no Cache-Control
		// override. Nothing here for CloudFront/CDN caching to worry about,
		// since no Set-Cookie is ever written for this response.
		logger.info('[VWO FME] Detected bot — excluding from VWO, no visitor ID minted');
		return visitorId; // empty string
	}

	logger.info(`[VWO FME] Generated new visitor ID: ${visitorId}`);
	const cookieValue = `${VWO_COOKIE_CONFIG.name}=${visitorId}; Max-Age=${VWO_COOKIE_CONFIG.maxAge}; Path=${
		VWO_COOKIE_CONFIG.path
	}; SameSite=${VWO_COOKIE_CONFIG.sameSite}${VWO_COOKIE_CONFIG.httpOnly ? '; HttpOnly' : ''}; Secure`;
	const existingHeader = res.getHeader('Set-Cookie');
	if (existingHeader) {
		const headers = Array.isArray(existingHeader) ? existingHeader : [existingHeader as string];
		res.setHeader('Set-Cookie', [...headers, cookieValue]);
	} else {
		res.setHeader('Set-Cookie', cookieValue);
	}

	// Prevent shared-cache poisoning: pages that mint a visitor ID must not be
	// stored by the CDN, or every subsequent cache hit would reuse this ID.
	// Repeat visitors carry the cookie on the request and skip this branch, so
	// the SHORT_TTL cache still applies to them.
	res.setHeader('Cache-Control', 'private, no-store');

	return visitorId;
}
